/**
 * Single source of truth for "is this a transient network / infra blip rather
 * than an application bug?". Consolidates three former copies that had drifted
 * apart (chat browser-fetch patterns, Airtable server 5xx/TCP codes, and the
 * auth-wrapper `AuthRetryableFetchError` check) into one union detector.
 *
 * Use it to decide whether to retry, or to skip Sentry capture for best-effort
 * / fire-and-forget work where a transient failure is non-actionable noise.
 *
 * Covered signals (case-insensitive), the exact union of the prior three impls:
 * - Browser fetch failures (supabase-js / postgrest-js surface these as a
 *   returned `{ error }` rather than a throw):
 *     Chrome  "TypeError: Failed to fetch"
 *     Safari  "Load failed"
 *     Firefox "NetworkError when attempting to fetch resource"
 *     Aborted "The operation was aborted" / "AbortError"
 * - Server-side HTTP 5xx + Node/TCP errors (Airtable + fetch):
 *     502 / 503 / 504, ETIMEDOUT, ECONNRESET, ECONNREFUSED, UNEXPECTED_ERROR
 * - Auth check retry: supabase-js raises `AuthRetryableFetchError` for a
 *   network / timeout / 5xx failure of `auth.getUser()`.
 */
const TRANSIENT_NETWORK_RE =
  /failed to fetch|fetch failed|load failed|networkerror|network request failed|the operation was aborted|aborterror|\b(?:502|503|504|etimedout|econnreset|econnrefused|unexpected_error)\b/i;

function messageOf(error: unknown): string {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (
    typeof error === 'object' &&
    'message' in error &&
    typeof (error as { message?: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message;
  }
  return '';
}

function nameOf(error: unknown): string {
  if (
    error &&
    typeof error === 'object' &&
    'name' in error &&
    typeof (error as { name?: unknown }).name === 'string'
  ) {
    return (error as { name: string }).name;
  }
  return '';
}

/**
 * Returns true when the error is an expected, transient network / infra failure
 * (retry-worthy, and non-actionable as a bug). Accepts any thrown value, a
 * supabase-js `{ error }` object, or `null` / `undefined`.
 */
export function isTransientNetworkError(error: unknown): boolean {
  if (nameOf(error) === 'AuthRetryableFetchError') return true;

  const message = messageOf(error);
  if (!message) return false;

  return TRANSIENT_NETWORK_RE.test(message);
}
